import { useFontSize } from "@/contexts/font-size/useFontSize";
import { useMountEffect } from "@/hooks/use-mount-effect";
import { useResizeObserver } from "@/hooks/use-resize-observer";
import { getBaseFontSizeFromContainerDimensions } from "@/utils/scaling";
import { useRef, useState } from "react";

import styles from "./ScaledContainer.module.css";

interface ScaledContainerProps {
  children: React.ReactNode;
  className?: string;
}

export const ScaledContainer: React.FC<ScaledContainerProps> = ({
  children,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const readyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isReadyRef = useRef<boolean>(false);
  const [baseFontSize, setBaseFontSize] = useState<number>(16);
  const [isReady, setIsReady] = useState<boolean>(false);
  const { setBaseFontSize: setGlobalFontSize } = useFontSize();

  // React Compiler handles optimization
  const handleResize = (entries: ResizeObserverEntry[]) => {
    const entry = entries[0];
    if (entry) {
      const { width, height } = entry.contentRect;
      const newFontSize = getBaseFontSizeFromContainerDimensions({
        width,
        height,
      });
      setBaseFontSize(newFontSize);
      setGlobalFontSize(newFontSize);

      // Mark as ready after first measurement with a small delay
      if (!isReadyRef.current && !readyTimeoutRef.current) {
        readyTimeoutRef.current = setTimeout(() => {
          isReadyRef.current = true;
          setIsReady(true);
          readyTimeoutRef.current = null;
        }, 400); // 400ms delay to ensure layout is stable
      }
    }
  };

  useMountEffect(() => {
    return () => {
      if (readyTimeoutRef.current) {
        clearTimeout(readyTimeoutRef.current);
      }
    };
  });

  // Initialize resize observer once
  useResizeObserver(containerRef as React.RefObject<HTMLElement>, handleResize);

  // React Compiler handles optimization
  const containerClasses = [
    styles.scaledContainer,
    isReady && styles.ready,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const inlineStyles = { fontSize: `${baseFontSize}px` };

  return (
    <main ref={containerRef} className={containerClasses} style={inlineStyles}>
      {children}
    </main>
  );
};
